<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useRoute } from 'vue-router'

import { menuRoutes } from '@/router'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'

const emit = defineEmits(['open-nav'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const route = useRoute()
const { isMobile } = useDetectDevice()

const props = defineProps({
  color: {
    type: String,
    default: 'primary'
  }
})

const fillBreadcrumb = (menuEntries, menuItem, crumbs) => {
  if (menuItem) {
    const crumb = {
      label: i18n.t(menuItem.code, 2),
      to: menuItem.route,
    }
    if (menuItem.icon) {
      crumb.icon = menuItem.icon
    }
    crumb.color = props.color
    crumbs.unshift(crumb)
    if (menuItem.parent_id) {
      const parentItem = menuEntries.find(item => item.id === menuItem.parent_id)
      fillBreadcrumb(menuEntries, parentItem, crumbs)
    }
  }
}

const breadcrumbEntries = computed(() => {
  const crumbs = []
  const menuEntries = webSiteStore.getMenuEntriesWithRoute(true)
  const currentMenuEntry = menuEntries.find(item => item.route === menuRoutes[route.meta.reference])

  fillBreadcrumb(menuEntries, currentMenuEntry, crumbs)

  if (webSiteStore.breadcrumbSuffix) {
    crumbs.push({ label: webSiteStore.breadcrumbSuffix, color: props.color })
  }

  return crumbs
})

const breadcrumbsRef = ref(null)
const isCollapsed = ref(false)

const canCollapse = computed(() => isMobile.value && breadcrumbEntries.value.length > 1)

const visibleEntries = computed(() =>
  isCollapsed.value ? breadcrumbEntries.value.slice(-1) : breadcrumbEntries.value
)

const entryStyle = computed(() => {
  if (isCollapsed.value) return { flex: '1 1 0', minWidth: 0, color: props.color }
  if (isMobile.value) return { flex: '0 0 auto', color: props.color }
  return { flexGrow: 1, flexFlow: 'row', color: props.color }
})

const updateCollapsed = async() => {
  if (!canCollapse.value) {
    isCollapsed.value = false
    return
  }
  isCollapsed.value = false
  await nextTick()
  const el = breadcrumbsRef.value?.$el
  if (!el) return
  isCollapsed.value = el.scrollWidth > el.clientWidth + 1
}

onMounted(() => {
  updateCollapsed()
  window.addEventListener('resize', updateCollapsed)
})
onBeforeUnmount(() => window.removeEventListener('resize', updateCollapsed))
watch(breadcrumbEntries, updateCollapsed)
</script>
<template>
  <q-breadcrumbs
    ref="breadcrumbsRef"
    :class="['breadcrumbs', { 'breadcrumbs--clamped': isMobile, 'breadcrumbs--collapsed': isCollapsed }]"
  >
    <template #separator>
      <q-icon
        size="1rem"
        name="mdi-chevron-right"
        :color="props.color"
      />
    </template>
    <q-breadcrumbs-el
      v-if="isCollapsed"
      class="breadcrumb-collapsed"
      icon="mdi-dots-horizontal"
      :style="{ color: props.color }"
      @click="emit('open-nav')"
    />
    <q-breadcrumbs-el
      v-for="(crumb, index) in visibleEntries"
      :key="index"
      class="breadcrumb-entry"
      :label="crumb.label"
      :to="crumb.to"
      :icon="isMobile ? undefined : crumb.icon"
      :style="entryStyle"
    />
  </q-breadcrumbs>
</template>
<style lang="scss" scoped>
.breadcrumbs {
  display: flex;
  flex-flow: row wrap;

  & :deep(div) {
    flex-flow: row;
  }
}

.breadcrumbs--clamped {
  flex-flow: row nowrap;
  min-width: 0;
  overflow: hidden;

  .breadcrumb-entry {
    min-width: 0;

    :deep(.q-breadcrumbs__el-label) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.breadcrumbs--collapsed .breadcrumb-collapsed {
  flex: 0 0 auto;
  cursor: pointer;
}
</style>
